import { mediaQueryLarge, onDocumentReady, prefersReducedMotion } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

const SECTION_REVEAL_SELECTORS = [
  '.section-resource-list__header',
  '.pet-category-cards__header',
  '.pet-category-card',
  '.pet-benefits-bar__grid--desktop .pet-benefits-bar__item',
  '.pet-benefits-bar__marquee-content > .pet-benefits-bar__item:nth-child(-n + 4)',
  '.media-with-content',
  '.text-block',
].join(', ');

/** @type {IntersectionObserver | null} */
let observer = null;

/** @type {boolean} */
let enabled = false;

/**
 * @param {HTMLElement} element
 * @param {number} index
 */
function attachReveal(element, index) {
  if (element.classList.contains('scroll-reveal')) return;

  element.classList.add('scroll-reveal');
  element.style.setProperty('--scroll-reveal-delay', `${Math.min(index, 8) * 40}ms`);
  observer?.observe(element);
}

/**
 * @param {Element} root
 */
function scanProductItems(root) {
  if (!enabled || !observer) return;

  const main = root.closest('#MainContent') ?? root;
  if (!(main instanceof HTMLElement)) return;

  main.querySelectorAll('.product-grid, .resource-list').forEach((grid) => {
    grid.querySelectorAll(':scope > .product-grid__item, :scope > .resource-list__item').forEach(
      (element, index) => {
        if (element instanceof HTMLElement) attachReveal(element, index);
      }
    );
  });
}

/**
 * @param {Element} root
 */
function scanSections(root) {
  if (!enabled || !observer) return;

  const main = root.closest('#MainContent') ?? root;
  if (!(main instanceof HTMLElement)) return;

  const firstSection = main.querySelector(':scope > .shopify-section');
  const sections = main.querySelectorAll(':scope > .shopify-section');

  sections.forEach((section) => {
    if (section === firstSection) return;

    const matches = Array.from(section.querySelectorAll(SECTION_REVEAL_SELECTORS));
    const targets = matches.length ? matches : Array.from(section.querySelectorAll(':scope > .section'));

    targets.forEach((element, index) => {
      if (!(element instanceof HTMLElement) || element.classList.contains('scroll-reveal')) return;

      const hasRevealAncestor = matches.some(
        (candidate) => candidate !== element && candidate.contains(element)
      );
      if (hasRevealAncestor) return;

      attachReveal(element, index);
    });
  });
}

/**
 * @param {Element} root
 */
function scan(root) {
  scanProductItems(root);
  scanSections(root);
}

/**
 * @param {HTMLElement} element
 */
function revealElement(element) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      element.classList.add('scroll-reveal--visible');
      observer?.unobserve(element);
    });
  });
}

function setup() {
  if (prefersReducedMotion() || mediaQueryLarge.matches) return;

  enabled = true;

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target instanceof HTMLElement) {
          revealElement(entry.target);
        }
      }
    },
    {
      root: null,
      rootMargin: '0px 0px 20% 0px',
      threshold: 0,
    }
  );

  const main = document.getElementById('MainContent');
  if (main) scan(main);

  watchResultsList(main);
}

/**
 * @param {HTMLElement | null} main
 */
function watchResultsList(main) {
  if (!main) return;

  const resultsList = main.querySelector('results-list');
  if (!(resultsList instanceof HTMLElement)) return;

  if (resultsList.hasAttribute('initialized')) {
    scanProductItems(resultsList);
    return;
  }

  const resultsObserver = new MutationObserver(() => {
    if (!resultsList.hasAttribute('initialized')) return;
    resultsObserver.disconnect();
    scanProductItems(resultsList);
  });

  resultsObserver.observe(resultsList, {
    attributes: true,
    attributeFilter: ['initialized'],
  });
}

function teardown() {
  enabled = false;
  observer?.disconnect();
  observer = null;

  document.querySelectorAll('.scroll-reveal').forEach((element) => {
    element.classList.remove('scroll-reveal', 'scroll-reveal--visible');
    element.style.removeProperty('--scroll-reveal-delay');
  });
}

function handleBreakpointChange() {
  if (mediaQueryLarge.matches) {
    teardown();
  } else if (!prefersReducedMotion()) {
    setup();
  }
}

/**
 * @param {Event} event
 */
function handleSectionLoad(event) {
  if (!enabled || !(event.target instanceof Element)) return;
  scan(event.target);
}

/**
 * @param {Event} event
 */
function handleResultsUpdate(event) {
  if (!enabled) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  window.requestAnimationFrame(() => {
    scan(target.closest('#MainContent') ?? target);
  });
}

function init() {
  if (prefersReducedMotion()) return;

  setup();
  mediaQueryLarge.addEventListener('change', handleBreakpointChange);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener(StandardEvents.collectionUpdate, handleResultsUpdate);
  document.addEventListener(StandardEvents.searchUpdate, handleResultsUpdate);
}

onDocumentReady(init);
